import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText, Tooltip,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import BarChartIcon from "@mui/icons-material/BarChart";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import AppHeader from "./AppHeader";
import AppShell from "./AppShell";
import MainContentScrollArea from "./MainContentScrollArea";
import { APP_HEADER_HEIGHT } from "./layoutConstants";

const SIDEBAR_WIDTH = 64;

const NAV_ITEMS = [
  { id: "tenants",   label: "Tenant Management", icon: <DashboardIcon />,             path: "/platform/tenants" },
  { id: "new",       label: "New Tenant",          icon: <AddBusinessIcon />,           path: "/platform/tenants/new" },
  { id: "analytics", label: "Platform Analytics",  icon: <BarChartIcon />,              path: "/platform/analytics" },
  { id: "keys",      label: "Licence Keys",         icon: <VpnKeyIcon />,                path: "/platform/licence-keys" },
  { id: "audit",     label: "Audit Log",            icon: <HistoryIcon />,               path: "/platform/audit" },
  { id: "settings",  label: "Platform Settings",    icon: <SettingsIcon />,              path: "/platform/settings" },
  { id: "notif",     label: "Notifications",        icon: <NotificationsOutlinedIcon />, path: "/platform/notifications" },
];

export default function PlatformLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <AppShell>
      {/* Reuse the same app header */}
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1200 }}>
        <AppHeader showUtilityIcons={false} />
      </Box>

      {/* Sidebar — same style as ProjectsSidebar */}
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          borderRight: "1px solid #eee",
          height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
          position: "fixed",
          left: 0,
          top: APP_HEADER_HEIGHT,
          bgcolor: "#fafafa",
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 2,
        }}
      >
        <List sx={{ width: "100%", p: 0 }}>
          {NAV_ITEMS.map((item) => {
            const isSelected =
              item.path === "/platform/tenants/new"
                ? pathname === item.path
                : pathname.startsWith(item.path) && item.path !== "/platform/tenants/new";
            return (
              <Tooltip key={item.id} title={item.label} placement="right" arrow>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => navigate(item.path)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 2,
                    px: 0,
                    minHeight: 64,
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    "&.Mui-selected": {
                      backgroundColor: "#e3f2fd",
                      borderLeft: "3px solid #1976d2",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "auto",
                      color: isSelected ? "#1976d2" : "inherit",
                      justifyContent: "center",
                      mb: 0.5,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "10px",
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? "#1976d2" : "inherit",
                      textAlign: "center",
                    }}
                    sx={{ margin: 0 }}
                  />
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <MainContentScrollArea ml={SIDEBAR_WIDTH} bgcolor="#f9fafb">
        <Outlet />
      </MainContentScrollArea>
    </AppShell>
  );
}
